import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import VenueMapCanvas from './VenueMap';
import api from '../utils/Api';
import { AddressFields, VenueEntry } from '../utils/VenueMap';

import './VenueMapSettings.scss';

interface VenueMapSettingsProps {
  playerId: number;
  playerName: string;
  token: string;
  onNotice(tone: 'ok' | 'bad', text: string): void;
}

type Form = AddressFields & { displayName: string };

const FIELDS: Array<{ key: keyof Form; label: string; autoComplete: string; required?: boolean }> = [
  { key: 'displayName', label: 'Venue name', autoComplete: 'organization', required: true },
  { key: 'street', label: 'Street', autoComplete: 'address-line1', required: true },
  { key: 'city', label: 'City', autoComplete: 'address-level2', required: true },
  { key: 'region', label: 'State or region', autoComplete: 'address-level1' },
  { key: 'postalCode', label: 'Postal code', autoComplete: 'postal-code' },
  { key: 'country', label: 'Country', autoComplete: 'country-name', required: true },
];

const formOf = (entry: VenueEntry): Form => ({
  displayName: entry.displayName || '',
  street: entry.street || '',
  city: entry.city || '',
  region: entry.region || '',
  postalCode: entry.postalCode || '',
  country: entry.country || '',
});

const same = (a: Form, b: Form) => FIELDS.every(({ key }) => (a[key] || '').trim() === (b[key] || '').trim());

/**
 * The control panel's Map tab, owner only: put the venue on the public "Venues near you"
 * map. The owner types the address, we find it on the map, and they confirm the pin.
 */
const VenueMapSettings = ({ playerId, playerName, token, onNotice }: VenueMapSettingsProps) => {
  const [entry, setEntry] = useState<VenueEntry | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // What the last save found, to explain an address with no pin.
  const [outcome, setOutcome] = useState<VenueEntry['outcome']>(undefined);

  const show = (next: VenueEntry) => {
    setEntry(next);
    setForm(formOf(next));
    setOutcome(next.outcome);
  };

  useEffect(() => {
    api
      .venues(`player/${playerId}`, 'GET', token, null)
      .then(show)
      .catch(error => setLoadError(error.message));
  }, [playerId]);

  const act = async (work: () => Promise<VenueEntry>, done: (next: VenueEntry) => [('ok' | 'bad'), string]) => {
    setBusy(true);
    try {
      const next = await work();
      show(next);
      const [tone, text] = done(next);
      onNotice(tone, text);
    } catch (error) {
      onNotice('bad', error.message);
    } finally {
      setBusy(false);
    }
  };

  const setEnabled = (enabled: boolean) =>
    act(
      () => api.venues(`player/${playerId}`, 'PUT', token, { enabled, displayName: form ? form.displayName : playerName }),
      () => ['ok', enabled ? 'Turned on. Now add your address.' : 'Your venue is off the map.'],
    );

  const save = () =>
    act(
      () => api.venues(`player/${playerId}/address`, 'POST', token, form),
      next =>
        next.outcome === 'found'
          ? ['ok', 'Found it. Check the pin and confirm.']
          : next.outcome === 'unchanged'
            ? ['ok', 'Saved.']
            : next.outcome === 'lookup_failed'
              ? ['bad', 'We could not look up the address just now. Try again in a minute.']
              : ['bad', 'We could not find that address. Check it and save again.'],
    );

  const confirm = () =>
    act(() => api.venues(`player/${playerId}/confirm`, 'POST', token, {}), () => ['ok', 'Confirmed. Your venue is on the map.']);

  const pins = useMemo(
    () =>
      entry && entry.lat != null && entry.lng != null
        ? [{ id: 1, lat: entry.lat, lng: entry.lng, label: entry.displayName || playerName }]
        : [],
    [entry && entry.lat, entry && entry.lng, entry && entry.displayName],
  );

  if (loadError) {
    return <p className="jukebox-error">{loadError}</p>;
  }
  if (!entry || !form) {
    return <p className="jukebox-empty">Loading…</p>;
  }

  const edited = !same(form, formOf(entry));
  const missing = FIELDS.some(f => f.required && !String(form[f.key] || '').trim());
  const hasPin = entry.lat != null && entry.lng != null;
  const notFound = entry.enabled && !hasPin && !!entry.street;

  return (
    <div className="control-rules venue-settings">
      <p className="control-hint">
        Put your venue on the public <Link to="/venues">Venues near you</Link> map, so people nearby can find you.
        Only its name, address and logo are shown.
      </p>

      <div className="control-rule">
        <button
          type="button"
          role="switch"
          aria-checked={entry.enabled}
          className={entry.enabled ? 'control-switch is-on' : 'control-switch'}
          disabled={busy}
          onClick={() => setEnabled(!entry.enabled)}
        >
          <span className="control-switch-track" aria-hidden="true">
            <span className="control-switch-knob" />
          </span>
          <span className="control-rule-label">Show this venue on the Jookable map</span>
          <span className="control-switch-state">{entry.enabled ? 'On' : 'Off'}</span>
        </button>
        <div className="control-rule-help">Turn it off anytime and your venue leaves the map straight away.</div>
      </div>

      {entry.enabled && (
        <div className="control-rule">
          <div className={entry.status === 'confirmed' && !edited ? 'venue-status is-live' : 'venue-status'}>
            {entry.status === 'confirmed' && !edited ? 'On the map' : 'Your address'}
          </div>
          <div className="venue-fields">
            {FIELDS.map(({ key, label, autoComplete }) => (
              <label className="venue-field" key={key}>
                <span className="venue-field-label">{label}</span>
                <input
                  type="text"
                  value={form[key]}
                  autoComplete={autoComplete}
                  maxLength={key === 'displayName' ? 80 : 120}
                  disabled={busy}
                  onChange={e => setForm({ ...form, [key]: e.target.value })}
                />
              </label>
            ))}
          </div>
          <div className="venue-actions">
            <button type="button" className="control-payouts-button" disabled={busy || missing || (!edited && hasPin)} onClick={save}>
              {busy ? 'Saving…' : 'Save address'}
            </button>
          </div>
          {entry.status === 'confirmed' && (
            <div className="control-rule-help">A new address is found on the map again and needs confirming again.</div>
          )}

          {notFound && !edited && (
            <div className="jukebox-notice jukebox-notice-bad venue-not-found" role="status">
              {outcome === 'lookup_failed'
                ? 'We could not look up the address just now. Try saving again in a minute.'
                : 'We could not find that address on the map. Check the street, city and country, then save again.'}{' '}
              Your venue stays off the map until it is found.
            </div>
          )}
        </div>
      )}

      {entry.enabled && hasPin && !edited && (
        <div className="control-rule">
          <div className="venue-status">{entry.status === 'confirmed' ? 'Your pin' : 'Is this the right spot?'}</div>
          <VenueMapCanvas className="venue-settings-map" pins={pins} focusZoom={17} ariaLabel="Where the pin for your venue is" />
          {entry.status === 'pending' && (
            <>
              <div className="venue-actions">
                <button type="button" className="control-payouts-button" disabled={busy} onClick={confirm}>
                  Confirm
                </button>
              </div>
              <div className="control-rule-help">
                Your venue goes on the map once you confirm. If the pin is wrong, fix the address and save again.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VenueMapSettings;
